import DownloadArrowSvg from "@/src/assets/icons/icon-arrow-download.svg";
import { Button } from "@/src/components/atom/Button";
import Checkbox from "@/src/components/atom/Checkbox";
import Dialog from "@/src/components/atom/Dialog";
import Icon from "@/src/components/atom/Icon";
import Loader from "@/src/components/atom/Loader";
import { renderNoRowsComponent } from "@/src/components/atom/Table";
import RadixTooltip from "@/src/components/atom/Tooltip/RadixTooltip";
import Typo from "@/src/components/atom/Typo";
import BreakWordTypo from "@/src/components/molecule/BreakWordTypo";
import Flex from "@/src/components/molecule/Flex";
import LoadingPhotoPreviewDialog from "@/src/components/organism/LoadingPhotoPreviewDialog";
import { downloadFile } from "@/src/utils/downloadFile";
import { toPascalCase } from "@/src/utils/transform";
import dayjs from "dayjs";
import { ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useContainerPhotosDialog } from "./hooks";
import {
  InnerContent,
  FlexBox,
  FixedWidthFlexItem,
  RestWidthFlexItem,
  TabsContainer,
  Tabs,
  StyledTabItem,
  AllContainerCell,
  ContainerList,
  ContainerItem,
  DownloadButton,
  MiddleTitleSection,
  MiddleRestSection,
  ContainerItemArticle,
  StyledImage,
  ImageSection,
  InformationTitleSection,
  Divider,
  InformationItemContainer,
  FlexCenterContainer,
  NoDataContainer,
  TitleWithSubtitle,
  SubtitleText,
  ImageDownloadContainer,
  InfoIcon,
  TooltipContent,
  TooltipContainer,
  DownloadIcon,
} from "./styles";
import {
  ContainerPhotosDialogProps,
  ContainerMediaData,
  ContainerMediaInfo,
  SelectedImageData,
  DialogTab,
  MediaByPhotoType,
  MediaByPhotoTypeItem,
  CustomPhotoTypeGroupItem,
} from "./types";
import { ContainerMediaType } from "@/src/store/apis/tasks/taskDetail/interface";

// ============================================================================
// Pure utility functions (extracted for optimization and testability)
// ============================================================================

/**
 * Calculate total media counts from container data
 * Uses photoTypeMedias if available, falls back to legacy count fields
 */
const calculateMediaTotals = (
  data: ContainerMediaData | undefined
): { totalMax: number; totalUploaded: number } => {
  if (!data) return { totalMax: 0, totalUploaded: 0 };

  // Use photoTypeMedias if available
  if (data.photoTypeMedias && Object.keys(data.photoTypeMedias).length > 0) {
    let totalMax = 0;
    let totalUploaded = 0;

    for (const [key, value] of Object.entries(data.photoTypeMedias)) {
      if (key === "CUSTOM" && Array.isArray(value)) {
        // CUSTOM is an array - sum up all items
        for (const item of value) {
          totalMax += item.requiredCount;
          totalUploaded += item.uploadedCount;
        }
      } else if (!Array.isArray(value)) {
        // Standard photo type
        totalMax += value.requiredCount;
        totalUploaded += value.uploadedCount;
      }
    }

    return { totalMax, totalUploaded };
  }

  // Fallback to legacy count fields
  return {
    totalMax:
      data.containerSampleBaleMediasCount +
      data.containerEmptyMediasCount +
      data.containerQuarterLoadedMediasCount +
      data.containerHalfLoadedMediasCount +
      data.containerFullLoadedMediasCount +
      data.containerOneDoorClosedMediasCount +
      data.containerBothDoorsClosedMediasCount +
      data.containerCloseUpSealMediasCount +
      data.containerExtraMediasCount,
    totalUploaded:
      data.uploadedContainerSampleBaleMediasCount +
      data.uploadedContainerEmptyMediasCount +
      data.uploadedContainerQuarterLoadedMediasCount +
      data.uploadedContainerHalfLoadedMediasCount +
      data.uploadedContainerFullLoadedMediasCount +
      data.uploadedContainerOneDoorClosedMediasCount +
      data.uploadedContainerBothDoorsClosedMediasCount +
      data.uploadedContainerCloseUpSealMediasCount +
      data.uploadedContainerExtraMediasCount,
  };
};

/**
 * Legacy media group configuration with proper ContainerMediaType typing
 */
interface LegacyMediaGroup {
  type: ContainerMediaType;
  getList: (data: ContainerMediaData) => ContainerMediaData["sampleBaleMediaInfoList"];
  getMax: (data: ContainerMediaData) => number;
  getUploaded: (data: ContainerMediaData) => number;
}

const LEGACY_MEDIA_GROUPS: LegacyMediaGroup[] = [
  { type: "SAMPLE_BALE", getList: (d) => d.sampleBaleMediaInfoList, getMax: (d) => d.containerSampleBaleMediasCount, getUploaded: (d) => d.uploadedContainerSampleBaleMediasCount },
  { type: "EMPTY", getList: (d) => d.emptyMediaInfoList, getMax: (d) => d.containerEmptyMediasCount, getUploaded: (d) => d.uploadedContainerEmptyMediasCount },
  { type: "QUARTER_LOADED", getList: (d) => d.quarterLoadedMediaInfoList, getMax: (d) => d.containerQuarterLoadedMediasCount, getUploaded: (d) => d.uploadedContainerQuarterLoadedMediasCount },
  { type: "HALF_LOADED", getList: (d) => d.halfLoadedMediaInfoList, getMax: (d) => d.containerHalfLoadedMediasCount, getUploaded: (d) => d.uploadedContainerHalfLoadedMediasCount },
  { type: "FULL_LOADED", getList: (d) => d.fullLoadedMediaInfoList, getMax: (d) => d.containerFullLoadedMediasCount, getUploaded: (d) => d.uploadedContainerFullLoadedMediasCount },
  { type: "ONE_DOOR_CLOSED", getList: (d) => d.oneDoorClosedMediaInfoList, getMax: (d) => d.containerOneDoorClosedMediasCount, getUploaded: (d) => d.uploadedContainerOneDoorClosedMediasCount },
  { type: "BOTH_DOORS_CLOSED", getList: (d) => d.bothDoorsClosedMediaInfoList, getMax: (d) => d.containerBothDoorsClosedMediasCount, getUploaded: (d) => d.uploadedContainerBothDoorsClosedMediasCount },
  { type: "CLOSE_UP_SEAL", getList: (d) => d.closeUpSealMediaInfoList, getMax: (d) => d.containerCloseUpSealMediasCount, getUploaded: (d) => d.uploadedContainerCloseUpSealMediasCount },
  { type: "EXTRA", getList: (d) => d.extraMediaInfoList, getMax: (d) => d.containerExtraMediasCount, getUploaded: (d) => d.uploadedContainerExtraMediasCount },
];

const PHOTO_TYPE_MAPPING: Record<string, keyof MediaByPhotoType> = {
  SAMPLE_BALE: "sampleBale",
  EMPTY: "empty",
  QUARTER_LOADED: "quarterLoaded",
  HALF_LOADED: "halfLoaded",
  FULL_LOADED: "fullLoaded",
  ONE_DOOR_CLOSED: "oneDoorClosed",
  BOTH_DOORS_CLOSED: "bothDoorsClosed",
  CLOSE_UP_SEAL: "closeUpSeal",
  EXTRA: "extra",
};

const getPhotoTypeTitleFromData = (
  mediaByPhotoType: MediaByPhotoType | undefined,
  typeCode: ContainerMediaType,
  fallbackLabel: string
): string => {
  if (!mediaByPhotoType) return fallbackLabel;

  const key = PHOTO_TYPE_MAPPING[typeCode];
  if (!key) return fallbackLabel;

  const items = mediaByPhotoType[key] as MediaByPhotoTypeItem[] | undefined;
  return items?.[0]?.title ?? fallbackLabel;
};

/**
 * ContainerPhotosDialog - 통합 컨테이너 사진 다이얼로그
 *
 * 5가지 모드를 지원합니다:
 * - exporter-shipment: 수출자 선적 상세
 * - importer-contract: 수입자 계약 상세
 * - exporter-task: 수출자 태스크 상세
 * - exporter-single: 수출자 단일 컨테이너
 * - shared-email: 공유 이메일 미리보기
 *
 * @example
 * // 수출자 선적 상세
 * <ContainerPhotosDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   mode="exporter-shipment"
 *   params={{ mode: "exporter-shipment", shipmentId: 123 }}
 * />
 */
const ContainerPhotosDialog = ({
  open,
  onClose,
  params,
  initialSelectedContainer,
}: ContainerPhotosDialogProps) => {
  const { t } = useTranslation();
  const [isPreviewDownloadLoading, setIsPreviewDownloadLoading] =
    useState(false);

  const dialog = useContainerPhotosDialog(params, initialSelectedContainer);

  // Preview download handler
  const handlePreviewDownloadClick = useCallback(async () => {
    if (!dialog.selectedImage) return;

    try {
      setIsPreviewDownloadLoading(true);
      await downloadFile(
        dialog.selectedImage.src,
        dialog.selectedImage.originalFileName ?? ""
      );
    } finally {
      setIsPreviewDownloadLoading(false);
    }
  }, [dialog.selectedImage]);

  // Render tab content (container or photoType list)
  const renderCheckboxListByTabType = () => {
    if (dialog.activeTab === "container") {
      return (
        <>
          <AllContainerCell>
            <Checkbox
              disabled={
                dialog.isContainerListLoading ||
                !dialog.containerList?.length
              }
              checked={dialog.containerSelection.isAllSelected}
              onChange={(e) => {
                if (e.target.checked) {
                  dialog.containerSelection.addAllContainers();
                } else {
                  dialog.containerSelection.removeAllContainers();
                }
              }}
            />
            <Typo color="gray4" typoType="b7r">
              {t("common:containerNo")}
            </Typo>
          </AllContainerCell>

          <ContainerList>
            {dialog.isContainerListLoading ? (
              <FlexCenterContainer>
                <Loader />
              </FlexCenterContainer>
            ) : (
              <>
                {dialog.containerList?.map(({ id, containerNo }) => (
                  <ContainerItem
                    key={id}
                    className="padding-12"
                    data-selected={id === dialog.selectedSingleContainer}
                    onClick={() => dialog.setSelectedSingleContainer(id)}
                  >
                    {/*
                      onChange is empty because toggle logic is in onClick with stopPropagation.
                      This pattern allows clicking the checkbox without triggering parent's onClick.
                      Empty onChange prevents React controlled component warning.
                    */}
                    <Checkbox
                      checked={dialog.containerSelection.selectedContainers.includes(id)}
                      style={{ flexShrink: 0 }}
                      onChange={() => {}}
                      onClick={(e) => {
                        e.stopPropagation();
                        dialog.containerSelection.toggleContainer(id);
                      }}
                    />
                    <Typo color="gray4" typoType="b7r" className="ellipsis-label">
                      {containerNo ?? "-"}
                    </Typo>
                  </ContainerItem>
                ))}
              </>
            )}
          </ContainerList>

          {!dialog.isContainerListLoading &&
            !!dialog.containerList?.length &&
            dialog.config.showDownloadButton && (
              <DownloadButton
                onClick={dialog.downloadSelectedContainers}
                disabled={dialog.isContainerDownloading}
              >
                {dialog.isContainerDownloading ? (
                  <ImageDownloadContainer>
                    <Loader />
                  </ImageDownloadContainer>
                ) : (
                  <>
                    <Icon iconSrc={DownloadArrowSvg} iconSize={18} />
                    {t(dialog.config.downloadButtonKey)}
                  </>
                )}
              </DownloadButton>
            )}
        </>
      );
    }

    // Photo Type tab
    return (
      <>
        <AllContainerCell>
          <Checkbox
            disabled={dialog.isMediaByPhotoTypeLoading}
            checked={dialog.photoTypeSelection.isAllSelected}
            onChange={(e) => {
              if (e.target.checked) {
                dialog.photoTypeSelection.addAllPhotoTypes();
              } else {
                dialog.photoTypeSelection.removeAllPhotoTypes();
              }
            }}
          />
          <Typo color="gray4" typoType="b7r">
            {t("common:photoType")}
          </Typo>
        </AllContainerCell>

        <ContainerList>
          {dialog.isMediaByPhotoTypeLoading ? (
            <FlexCenterContainer>
              <Loader />
            </FlexCenterContainer>
          ) : (
            <>
              {dialog.availablePhotoTypes.map((typeCode) => {
                let label: string;
                if (typeCode.startsWith("CUSTOM_")) {
                  const customId = parseInt(typeCode.replace("CUSTOM_", ""), 10);
                  const customItem = dialog.mediaByPhotoType?.customs?.find(
                    (c) => c.photoTypeRequestId === customId
                  );
                  label = customItem?.title ?? typeCode;
                } else {
                  label = getPhotoTypeTitleFromData(
                    dialog.mediaByPhotoType,
                    typeCode as ContainerMediaType,
                    dialog.getPhotoTypeLabel(typeCode as ContainerMediaType)
                  );
                }
                return (
                  <ContainerItem
                    key={typeCode}
                    className="padding-12"
                    data-selected={typeCode === dialog.selectedSinglePhotoType}
                    onClick={() => dialog.setSelectedSinglePhotoType(typeCode as ContainerMediaType)}
                  >
                    <Checkbox
                      checked={dialog.photoTypeSelection.selectedPhotoTypes.includes(typeCode as ContainerMediaType)}
                      style={{ flexShrink: 0 }}
                      onChange={() => {}}
                      onClick={(e) => {
                        e.stopPropagation();
                        dialog.photoTypeSelection.togglePhotoType(typeCode as ContainerMediaType);
                      }}
                    />
                    <Typo color="gray4" typoType="b7r" className="ellipsis-label">
                      {label}
                    </Typo>
                  </ContainerItem>
                );
              })}
            </>
          )}
        </ContainerList>

        {!dialog.isMediaByPhotoTypeLoading && dialog.config.showDownloadButton && (
          <DownloadButton
            onClick={dialog.downloadSelectedPhotoTypes}
            disabled={dialog.isPhotoTypeDownloading}
          >
            {dialog.isPhotoTypeDownloading ? (
              <ImageDownloadContainer>
                <Loader />
              </ImageDownloadContainer>
            ) : (
              <>
                <Icon iconSrc={DownloadArrowSvg} iconSize={18} />
                {t(dialog.config.downloadButtonKey)}
              </>
            )}
          </DownloadButton>
        )}
      </>
    );
  };

  const renderImageListByTabType = () => {
    if (dialog.activeTab === "container") {
      return renderContainerTabContent();
    }
    return renderPhotoTypeTabContent();
  };

  const renderContainerTabContent = () => {
    const containerMedia = dialog.containerMedia;
    const isLoading = dialog.isContainerMediaLoading;
    const { totalMax, totalUploaded } = calculateMediaTotals(containerMedia);

    return (
      <RestWidthFlexItem>
        <MiddleTitleSection>
          <p className="ellipsis-header">
            <Typo typoType="h6">
              {isLoading ? "-" : (containerMedia?.containerNo ?? "-")}
            </Typo>
          </p>
          <p>
            <Typo typoType="b8m" color="gray5" className="flex-shrink-0">
              {t("common:total")} {totalUploaded} / {totalMax}
            </Typo>
          </p>
        </MiddleTitleSection>

        <MiddleRestSection>
          {isLoading ? (
            <FlexCenterContainer>
              <Loader size={50} />
            </FlexCenterContainer>
          ) : (
            <>{renderPhotoTypeGroups(containerMedia, dialog.activeTab)}</>
          )}
        </MiddleRestSection>
      </RestWidthFlexItem>
    );
  };

  const renderCustomPhotoTypeContent = (customTypeCode: string) => {
    const isLoading = dialog.isMediaByPhotoTypeLoading;
    const mediaByPhotoType = dialog.mediaByPhotoType;
    const customId = parseInt(customTypeCode.replace("CUSTOM_", ""), 10);
    const customItems = mediaByPhotoType?.customs?.filter(
      (c) => c.photoTypeRequestId === customId
    ) ?? [];

    const totalUploaded = customItems.reduce(
      (sum, c) => sum + c.uploadedContainerMediasCount,
      0
    );
    const totalMax = customItems.reduce(
      (sum, c) => sum + c.requiredContainerMediasCount,
      0
    );

    const customTitle = customItems[0]?.title ?? customTypeCode;

    return (
      <RestWidthFlexItem>
        <MiddleTitleSection>
          <p className="ellipsis-header">
            <Typo typoType="h6">{isLoading ? "-" : customTitle}</Typo>
          </p>
          <p>
            <Typo typoType="b8m" color="gray5" className="flex-shrink-0">
              {t("common:total")} {totalUploaded} / {totalMax}
            </Typo>
          </p>
        </MiddleTitleSection>

        <MiddleRestSection>
          {isLoading ? (
            <FlexCenterContainer>
              <Loader size={50} />
            </FlexCenterContainer>
          ) : (
            <>
              {customItems.length === 0 ? (
                <NoDataContainer>{renderNoRowsComponent()}</NoDataContainer>
              ) : (
                customItems.map((container) => (
                  <ContainerItemArticle key={container.id} tabType="photoType">
                    <header>
                      <Typo typoType="h6" className="ellipsis-middle-header">
                        {container.containerNo ?? "-"}
                      </Typo>
                      <Typo
                        typoType="b8m"
                        color="gray5"
                        className="flex-shrink-0"
                      >
                        {t("common:total")} {container.uploadedContainerMediasCount}{" "}
                        / {container.requiredContainerMediasCount}
                      </Typo>
                    </header>

                    <ImageSection>
                      {container.shipmentContainerMediaInfoList?.length > 0 ? (
                        container.shipmentContainerMediaInfoList.map(
                          (media, idx) => (
                            <StyledImage
                              key={media.containerMediaId}
                              onClick={() =>
                                handleImageClick(
                                  media,
                                  container.containerNo,
                                  customTitle,
                                  idx
                                )
                              }
                            >
                              <img
                                src={media.mSizeImageUrl ?? media.mediaUrl}
                                alt={`${customTitle}_${idx}`}
                              />
                            </StyledImage>
                          )
                        )
                      ) : (
                        <NoDataContainer>{renderNoRowsComponent()}</NoDataContainer>
                      )}
                    </ImageSection>
                  </ContainerItemArticle>
                ))
              )}
            </>
          )}
        </MiddleRestSection>
      </RestWidthFlexItem>
    );
  };

  const renderPhotoTypeTabContent = () => {
    const isLoading = dialog.isMediaByPhotoTypeLoading;
    const selectedType = dialog.selectedSinglePhotoType as string | undefined;
    const mediaByPhotoType = dialog.mediaByPhotoType;

    if (selectedType?.startsWith("CUSTOM_")) {
      return renderCustomPhotoTypeContent(selectedType);
    }

    const getPhotoTypeData = (): MediaByPhotoTypeItem[] => {
      if (!mediaByPhotoType || !selectedType) return [];

      const key = PHOTO_TYPE_MAPPING[selectedType];
      return key ? (mediaByPhotoType[key] as MediaByPhotoTypeItem[]) ?? [] : [];
    };

    const containerData = getPhotoTypeData();
    const totalUploaded = containerData.reduce(
      (sum, c) => sum + c.uploadedContainerMediasCount,
      0
    );
    const totalMax = containerData.reduce(
      (sum, c) => sum + c.requiredContainerMediasCount,
      0
    );

    const photoTypeTitle = containerData[0]?.title ?? (selectedType ? dialog.getPhotoTypeLabel(selectedType as ContainerMediaType) : "-");

    return (
      <RestWidthFlexItem>
        <MiddleTitleSection>
          <p className="ellipsis-header">
            <Typo typoType="h6">
              {isLoading || !selectedType ? "-" : photoTypeTitle}
            </Typo>
          </p>
          <p>
            <Typo typoType="b8m" color="gray5" className="flex-shrink-0">
              {t("common:total")} {totalUploaded} / {totalMax}
            </Typo>
          </p>
        </MiddleTitleSection>

        <MiddleRestSection>
          {isLoading ? (
            <FlexCenterContainer>
              <Loader size={50} />
            </FlexCenterContainer>
          ) : (
            <>
              {containerData.length === 0 ? (
                <NoDataContainer>{renderNoRowsComponent()}</NoDataContainer>
              ) : (
                containerData.map((container) => (
                  <ContainerItemArticle
                    key={container.id}
                    tabType="photoType"
                  >
                    <header>
                      <Typo typoType="h6" className="ellipsis-middle-header">
                        {container.containerNo ?? "-"}
                      </Typo>
                      <Typo
                        typoType="b8m"
                        color="gray5"
                        className="flex-shrink-0"
                      >
                        {t("common:total")} {container.uploadedContainerMediasCount}{" "}
                        / {container.requiredContainerMediasCount}
                      </Typo>
                    </header>

                    <ImageSection>
                      {container.shipmentContainerMediaInfoList?.length > 0 ? (
                        container.shipmentContainerMediaInfoList.map(
                          (media, idx) => (
                            <StyledImage
                              key={media.containerMediaId}
                              onClick={() =>
                                handleImageClick(
                                  media,
                                  container.containerNo,
                                  selectedType ?? "UNKNOWN",
                                  idx
                                )
                              }
                            >
                              <img
                                src={media.mSizeImageUrl ?? media.mediaUrl}
                                alt={`${selectedType}_${idx}`}
                              />
                            </StyledImage>
                          )
                        )
                      ) : (
                        <NoDataContainer>
                          {renderNoRowsComponent()}
                        </NoDataContainer>
                      )}
                    </ImageSection>
                  </ContainerItemArticle>
                ))
              )}
            </>
          )}
        </MiddleRestSection>
      </RestWidthFlexItem>
    );
  };

  const renderCustomsPhotoTypeContent = () => {
    const isLoading = dialog.isMediaByPhotoTypeLoading;
    const mediaByPhotoType = dialog.mediaByPhotoType;
    const customsData = mediaByPhotoType?.customs ?? [];

    const groupedByTitle = customsData.reduce<
      Record<string, CustomPhotoTypeGroupItem[]>
    >((acc, item) => {
      const key = item.title || "Custom";
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    const totalUploaded = customsData.reduce(
      (sum, c) => sum + c.uploadedContainerMediasCount,
      0
    );
    const totalMax = customsData.reduce(
      (sum, c) => sum + c.requiredContainerMediasCount,
      0
    );

    return (
      <RestWidthFlexItem>
        <MiddleTitleSection>
          <p className="ellipsis-header">
            <Typo typoType="h6">
              {isLoading ? "-" : t("common:customPhotos")}
            </Typo>
          </p>
          <p>
            <Typo typoType="b8m" color="gray5" className="flex-shrink-0">
              {t("common:total")} {totalUploaded} / {totalMax}
            </Typo>
          </p>
        </MiddleTitleSection>

        <MiddleRestSection>
          {isLoading ? (
            <FlexCenterContainer>
              <Loader size={50} />
            </FlexCenterContainer>
          ) : (
            <>
              {Object.keys(groupedByTitle).length === 0 ? (
                <NoDataContainer>{renderNoRowsComponent()}</NoDataContainer>
              ) : (
                Object.entries(groupedByTitle).map(([title, items]) => (
                  <ContainerItemArticle key={title} tabType="photoType">
                    <header>
                      <TitleWithSubtitle>
                        <Typo typoType="h6" className="ellipsis-middle-header">
                          {title}
                          {items[0]?.subTitle && (
                            <SubtitleText typoType="b9r" color="gray6">
                              {items[0].subTitle}
                            </SubtitleText>
                          )}
                        </Typo>
                      </TitleWithSubtitle>
                      <Typo
                        typoType="b8m"
                        color="gray5"
                        className="flex-shrink-0"
                      >
                        {t("common:total")}{" "}
                        {items.reduce(
                          (sum, c) => sum + c.uploadedContainerMediasCount,
                          0
                        )}{" "}
                        /{" "}
                        {items.reduce(
                          (sum, c) => sum + c.requiredContainerMediasCount,
                          0
                        )}
                      </Typo>
                    </header>

                    {items.map((container) => (
                      <div key={container.id} style={{ marginTop: "12px" }}>
                        <Typo typoType="b7m" color="gray4">
                          {container.containerNo ?? "-"}
                        </Typo>
                        <ImageSection>
                          {container.shipmentContainerMediaInfoList?.length >
                          0 ? (
                            container.shipmentContainerMediaInfoList.map(
                              (media, idx) => (
                                <StyledImage
                                  key={media.containerMediaId}
                                  onClick={() =>
                                    handleImageClick(
                                      media,
                                      container.containerNo,
                                      title,
                                      idx
                                    )
                                  }
                                >
                                  <img
                                    src={media.mSizeImageUrl ?? media.mediaUrl}
                                    alt={`${title}_${idx}`}
                                  />
                                </StyledImage>
                              )
                            )
                          ) : (
                            <NoDataContainer>
                              {renderNoRowsComponent()}
                            </NoDataContainer>
                          )}
                        </ImageSection>
                      </div>
                    ))}
                  </ContainerItemArticle>
                ))
              )}
            </>
          )}
        </MiddleRestSection>
      </RestWidthFlexItem>
    );
  };

  // Helper to render a single photo type item
  const renderPhotoTypeItem = (
    data: ContainerMediaData,
    tabType: DialogTab,
    typeCode: string,
    info: {
      title?: string;
      subTitle?: string;
      uploadedCount: number;
      requiredCount: number;
      medias: ContainerMediaInfo[] | null;
      remark?: string;
    },
    keyPrefix?: string
  ) => {
    const key = keyPrefix ? `${keyPrefix}_${typeCode}` : typeCode;
    const medias = info.medias || [];

    return (
      <ContainerItemArticle key={key} tabType={tabType}>
        <header>
          <TitleWithSubtitle>
            <Typo typoType="h6" className="ellipsis-middle-header">
              {info.title ?? typeCode}
              {info.subTitle && (
                <SubtitleText typoType="b9r" color="gray6">
                  {info.subTitle}
                </SubtitleText>
              )}
              {typeCode === "EXTRA" && data.extraMediaRemark && (
                <TooltipContainer>
                  <RadixTooltip
                    content={
                      <TooltipContent>
                        <span>{t("common:remark")}</span>
                        <span>{data.extraMediaRemark}</span>
                      </TooltipContent>
                    }
                  >
                    <InfoIcon color="gray7" />
                  </RadixTooltip>
                </TooltipContainer>
              )}
            </Typo>
          </TitleWithSubtitle>
          <Typo typoType="b8m" color="gray5" className="flex-shrink-0">
            {t("common:total")} {info.uploadedCount} / {info.requiredCount}
          </Typo>
        </header>

        <ImageSection>
          {medias.length > 0 ? (
            medias.map((media, idx) => (
              <StyledImage
                key={media.containerMediaId}
                onClick={() =>
                  handleImageClick(media, data.containerNo, typeCode, idx)
                }
              >
                <img
                  src={media.mSizeImageUrl ?? media.mediaUrl}
                  alt={`${typeCode}_${idx}`}
                />
              </StyledImage>
            ))
          ) : (
            <NoDataContainer>{renderNoRowsComponent()}</NoDataContainer>
          )}
        </ImageSection>
      </ContainerItemArticle>
    );
  };

  // Render photo type groups with images
  const renderPhotoTypeGroups = (
    data: ContainerMediaData | undefined,
    tabType: DialogTab
  ) => {
    if (!data) return null;

    // Use photoTypeMedias if available
    if (data.photoTypeMedias && Object.keys(data.photoTypeMedias).length > 0) {
      // Flatten entries: standard types as-is, CUSTOM array items individually
      type PhotoTypeItem = {
        typeCode: string;
        displayOrder: number;
        info: {
          title?: string;
          subTitle?: string;
          uploadedCount: number;
          requiredCount: number;
          medias: ContainerMediaInfo[] | null;
          remark?: string;
        };
        keyPrefix?: string;
      };

      const allItems: PhotoTypeItem[] = [];

      for (const [typeCode, value] of Object.entries(data.photoTypeMedias)) {
        if (typeCode === "CUSTOM" && Array.isArray(value)) {
          // CUSTOM is an array of CustomPhotoTypeItemDto
          for (const customItem of value) {
            allItems.push({
              typeCode: `CUSTOM_${customItem.id}`,
              displayOrder: customItem.displayOrder ?? 999,
              info: {
                title: customItem.title,
                subTitle: customItem.subTitle,
                uploadedCount: customItem.uploadedCount,
                requiredCount: customItem.requiredCount,
                medias: customItem.medias,
                remark: customItem.remark,
              },
              keyPrefix: "CUSTOM",
            });
          }
        } else if (!Array.isArray(value)) {
          // Standard photo type (PhotoTypeMediaInfoDto)
          allItems.push({
            typeCode,
            displayOrder: value.displayOrder ?? 999,
            info: value,
          });
        }
      }

      // Sort by displayOrder
      allItems.sort((a, b) => a.displayOrder - b.displayOrder);

      return allItems.map((item) =>
        renderPhotoTypeItem(
          data,
          tabType,
          item.typeCode,
          item.info,
          item.keyPrefix
        )
      );
    }

    // Fallback to legacy media lists
    return renderLegacyPhotoTypeGroups(data, tabType);
  };

  // Render legacy photo type groups (for backward compatibility)
  // Uses LEGACY_MEDIA_GROUPS constant for proper ContainerMediaType typing
  const renderLegacyPhotoTypeGroups = (
    data: ContainerMediaData,
    tabType: DialogTab
  ) => {
    // Filter groups that have media to show (max > 0)
    const activeGroups = LEGACY_MEDIA_GROUPS.filter(
      (group) => group.getMax(data) > 0
    );

    return activeGroups.map(({ type, getList, getMax, getUploaded }) => {
      const list = getList(data);
      const max = getMax(data);
      const uploaded = getUploaded(data);

      return (
        <ContainerItemArticle key={type} tabType={tabType}>
          <header>
            <Typo typoType="h6" className="ellipsis-middle-header">
              {dialog.getPhotoTypeLabel(type)}
              {type === "EXTRA" && data.extraMediaRemark && (
                <TooltipContainer>
                  <RadixTooltip
                    content={
                      <TooltipContent>
                        <span>{t("common:remark")}</span>
                        <span>{data.extraMediaRemark}</span>
                      </TooltipContent>
                    }
                  >
                    <InfoIcon color="gray7" />
                  </RadixTooltip>
                </TooltipContainer>
              )}
            </Typo>
            <Typo typoType="b8m" color="gray5" className="flex-shrink-0">
              {t("common:total")} {uploaded} / {max}
            </Typo>
          </header>

          <ImageSection>
            {list && list.length > 0 ? (
              list.map((media, idx) => (
                <StyledImage
                  key={media.containerMediaId}
                  onClick={() =>
                    handleImageClick(media, data.containerNo, type, idx)
                  }
                >
                  <img
                    src={media.mSizeImageUrl ?? media.mediaUrl}
                    alt={`${type}_${idx}`}
                  />
                </StyledImage>
              ))
            ) : (
              <NoDataContainer>{renderNoRowsComponent()}</NoDataContainer>
            )}
          </ImageSection>
        </ContainerItemArticle>
      );
    });
  };

  // Handle image click for preview
  const handleImageClick = (
    media: { mediaUrl: string; createdAt: string; coordinate?: any },
    containerNo: string | null,
    typeCode: string,
    idx: number
  ) => {
    const imageData: SelectedImageData = {
      src: media.mediaUrl,
      createdAt: media.createdAt,
      latitude: media.coordinate?.latitude,
      longitude: media.coordinate?.longitude,
      zoomLevel: media.coordinate?.zoomLevel
        ? Number(media.coordinate.zoomLevel)
        : undefined,
      mediaCoordinateRange: media.coordinate?.mediaCoordinateRange,
      originalFileName: `${containerNo ?? "-"}_${toPascalCase(typeCode)}${String(
        idx + 1
      ).padStart(2, "0")}.jpg`,
    };
    dialog.setSelectedImage(imageData);
  };

  // Render information panel (right side)
  const renderInformation = () => {
    const data = dialog.containerMedia;
    const isLoading = dialog.isContainerMediaLoading;

    const fields: Array<{
      label: string;
      value: ReactNode;
      isTooltip?: boolean;
      isDivider?: boolean;
    }> = [
      {
        label: t("common:containerNo"),
        value: (
          <Typo typoType="h6" color="blue2">
            {isLoading ? "-" : (data?.containerNo ?? "-")}
          </Typo>
        ),
      },
      {
        label: t("common:sealNo"),
        value: (
          <Typo typoType="h6" color="blue2">
            {isLoading ? "-" : (data?.sealNo ?? "-")}
          </Typo>
        ),
      },
      { label: "", value: "", isDivider: true },
      {
        label: t("shipment:exporter.dialog.label.workingDay"),
        value: isLoading
          ? "-"
          : data?.workingDayAt
          ? dayjs(data.workingDayAt).format("YYYY-MM-DD")
          : "-",
      },
      {
        label: t("shipment:exporter.dialog.label.factory"),
        value: isLoading ? "-" : (data?.workplaceName ?? "-"),
        isTooltip: true,
      },
      {
        label: t("common:buyer"),
        value: isLoading ? "-" : (data?.buyerName ?? "-"),
        isTooltip: true,
      },
      {
        label: t("common:item"),
        value: isLoading ? "-" : (data?.item ?? "-"),
        isTooltip: true,
      },
      {
        label: t("common:itemCode"),
        value: isLoading ? "-" : (data?.itemCode ?? "-"),
        isTooltip: true,
      },
      {
        label: t("common:netWeight"),
        value: isLoading ? "-" : (data?.netWeight ?? "-"),
      },
    ];

    return (
      <>
        {fields.map((field, idx) => {
          if (field.isDivider) {
            return <Divider key={`divider-${idx}`} />;
          }
          return (
            <InformationItem
              key={field.label}
              label={field.label}
              value={field.value}
              isTooltip={field.isTooltip}
            />
          );
        })}
      </>
    );
  };

  // Render image preview dialog
  const renderPreviewDialog = () => {
    if (!dialog.selectedImage) return null;

    const { src, zoomLevel, ...rest } = dialog.selectedImage;

    return (
      <LoadingPhotoPreviewDialog
        open
        onOpenChange={() => dialog.setSelectedImage(undefined)}
        selectImage={{ ...rest, zoomLevel, mediaUrl: src }}
        zoomLevel={zoomLevel}
        photoDetailRightAccessory={
          <Button
            buttonGrade="tertiary"
            buttonColor="black"
            onClick={handlePreviewDownloadClick}
            isLoading={isPreviewDownloadLoading}
          >
            <Flex alignItems="center" gap={2}>
              <DownloadIcon />
              {t("common:download")}
            </Flex>
          </Button>
        }
      />
    );
  };

  return (
    <>
      <Dialog
        title={t(dialog.config.titleKey)}
        open={open}
        onOpenChange={() => onClose()}
        width={dialog.config.width}
      >
        <InnerContent>
          <FlexBox>
            {dialog.config.showContainerList && (
              <FixedWidthFlexItem>
                {dialog.config.showTabs && (
                  <TabsContainer>
                    <Tabs role="tablist">
                      <StyledTabItem
                        tabValue="container"
                        onFocusItem={() => {}}
                        tabIndex={dialog.activeTab === "container" ? 0 : -1}
                        onFocus={() => dialog.setActiveTab("container")}
                        onClick={() => dialog.setActiveTab("container")}
                        data-selected={dialog.activeTab === "container"}
                      >
                        <Typo typoType="b7m">
                          {t(dialog.config.tabLabels.container)}
                        </Typo>
                      </StyledTabItem>
                      <StyledTabItem
                        tabValue="photoType"
                        onFocusItem={() => {}}
                        tabIndex={dialog.activeTab === "photoType" ? 0 : -1}
                        onFocus={() => dialog.setActiveTab("photoType")}
                        onClick={() => dialog.setActiveTab("photoType")}
                        data-selected={dialog.activeTab === "photoType"}
                      >
                        <Typo typoType="b7m">
                          {t(dialog.config.tabLabels.photoType)}
                        </Typo>
                      </StyledTabItem>
                    </Tabs>
                  </TabsContainer>
                )}

                {renderCheckboxListByTabType()}
              </FixedWidthFlexItem>
            )}

            {renderImageListByTabType()}

            {dialog.activeTab === "container" && dialog.config.showInformation && (
              <FixedWidthFlexItem>
                <InformationTitleSection>
                  <Typo typoType="h6">
                    {t("shipment:exporter.dialog.label.information")}
                  </Typo>
                </InformationTitleSection>

                <MiddleRestSection className="gap-24">
                  {renderInformation()}
                </MiddleRestSection>
              </FixedWidthFlexItem>
            )}
          </FlexBox>
        </InnerContent>
      </Dialog>

      {renderPreviewDialog()}
    </>
  );
};

export default ContainerPhotosDialog;

// Helper component for information items
const InformationItem = ({
  label,
  value,
  isTooltip,
}: {
  label: ReactNode;
  value: ReactNode;
  isTooltip?: boolean;
}) => {
  if (isTooltip) {
    return (
      <InformationItemContainer>
        <Typo typoType="b9m" color="gray6">
          {label}
        </Typo>
        <RadixTooltip
          content={
            <BreakWordTypo color="white" typoType="b10m">
              {value}
            </BreakWordTypo>
          }
        >
          <Typo typoType="h6" color="gray2" className="ellipsis">
            {value}
          </Typo>
        </RadixTooltip>
      </InformationItemContainer>
    );
  }

  return (
    <InformationItemContainer>
      <Typo typoType="b9m" color="gray6">
        {label}
      </Typo>
      <Typo typoType="h6" color="gray2" className="ellipsis">
        {value}
      </Typo>
    </InformationItemContainer>
  );
};

// Export types for consumers
export type { ContainerPhotosDialogProps, DialogParams, DialogMode } from "./types";
